import { BaseXHR } from 'utils/axios';

// ============ Types ============

export interface Airport {
  id: string;
  code: string;
  name: string;
  cityCode?: string;
  cityName?: string;
  countryCode?: string;
  countryName?: string;
}

export interface FareFamilyRule {
  id: string;
  code: string;
  name: string;
  description?: string;
  ruleType: string; // 'baggage', 'change', 'refund', etc.
  value: string;
  displayText?: string;
}

export interface FareFamily {
  id: string;
  code: string;
  name: string;
  fareBases: {
    code: string;
    description?: string;
  }[];
  rules: FareFamilyRule[];
}

export interface FareFamilyRulesParams {
  departureId: string;
  arrivalId: string;
  carrier?: string;
}

export interface FareFamilyRulesResponse {
  success: boolean;
  data: FareFamily[];
}

// API Response format: response.data.data = { fareFamilies: [], fareBases: [] }
export interface FareFamiliesApiResponse {
  success: boolean;
  data: {
    fareFamilies: FareFamilyData[];
    fareBases: any[];
  };
}

export interface FareFamilyData {
  id: number;
  code: string;
  name: string;
  cabinId: number;
  cabinName: string;
  status: number;
  fareBases: FareBasisItem[];
  fareFamilyRules: FareFamilyRuleItem[];
}

export interface FareBasisItem {
  id: number;
  fareFamilyId: number;
  code: string;
  name: string;
}

export interface FareFamilyRuleItem {
  id: number;
  code: string; // e.g., "FFR07"
  fareFamilyId: number;
  fareRuleId: number; // IDs: 8=hand baggage, 9=checked baggage, 3=refund, 2=change, etc.
  fareRuleName: string; // e.g., "Hành lý xách tay"
  departureId: number;
  departurePointName: string;
  departureCode: string;
  arriveId: number;
  arrivePointName: string;
  arriveCode: string;
  type: number; // 0 = included/free, 1 = paid/conditional, 2 = not allowed
  value: string | null; // e.g., "7kg", "1 kiện - 23kg"
  description: string | null; // e.g., "(1 kiện tối đa 7kg, 1 phụ kiện)"
  status: number; // 1 = active, 0 = inactive
}

// ============ API Functions ============

/**
 * SPA04 - Get list airports
 * Get airport information by code to retrieve airportID
 */
export const getAirportByCode = async (
  airportCode: string,
): Promise<Airport | null> => {
  try {
    const response = await BaseXHR.$get<{ success: boolean; data: Airport[] }>(
      'b2b/holiday/common/airport',
      { code: airportCode },
    );
    return response.data.data?.[0] || null;
  } catch (error) {
    console.error('Failed to get airport by code:', error);
    return null;
  }
};

/**
 * Get all Fare Families from the common API
 * GET /b2b/holiday/common/fare-families
 */
export const getAllFareFamilies = async (): Promise<FareFamilyData[]> => {
  try {
    const response = await BaseXHR.$get<FareFamiliesApiResponse>(
      'b2b/holiday/common/fare-families',
    );
    return response.data.data?.fareFamilies || [];
  } catch (error) {
    console.error('Failed to get fare families:', error);
    return [];
  }
};

/**
 * Get Fare Family Rules by route (legacy method)
 * Returns fare family rules for a specific route (departure -> arrival)
 */
export const getFareFamilyRules = async (
  params: FareFamilyRulesParams,
): Promise<FareFamily[]> => {
  try {
    const response = await BaseXHR.$post<FareFamilyRulesResponse>(
      'b2b/holiday/fare-family-rules',
      params,
    );
    return response.data.data || [];
  } catch (error) {
    console.error('Failed to get fare family rules:', error);
    return [];
  }
};

/**
 * Get fare family rules by fareBasis
 * Maps fareBasis (first 4 characters) to fare family and returns rules
 * Fallback: use cabinId if fareBasis not found
 */
export const getFareFamilyByFareBasis = (
  fareBasis: string,
  fareFamilies: FareFamilyData[],
  cabinId?: number,
): FareFamilyData | undefined => {
  // Extract first 4 characters of fareBasis
  const fareCode = fareBasis.substring(0, 4).toUpperCase();

  // First: try to find by fareBasis code (4 chars)
  let fareFamily = fareFamilies.find(family =>
    family.fareBases.some(fb => fb.code.toUpperCase() === fareCode),
  );

  // Fallback 1: try matching first char of fareBasis with fareBase code
  if (!fareFamily) {
    const firstChar = fareBasis.charAt(0).toUpperCase();
    fareFamily = fareFamilies.find(family =>
      family.fareBases.some(
        fb => fb.code.charAt(0).toUpperCase() === firstChar,
      ),
    );
  }

  // Fallback 2: use cabinId if provided
  if (!fareFamily && cabinId !== undefined) {
    fareFamily = fareFamilies.find(family => family.cabinId === cabinId);
  }

  return fareFamily;
};

/**
 * Hardcoded fareRuleId values for STG environment
 * NOTE: These IDs must be updated when deploying to Production
 */
export const FARE_RULE_IDS = {
  HAND_BAGGAGE: 8, // Hành lý xách tay
  CHECKED_BAGGAGE: 9, // Hành lý ký gửi
  REFUND: 3, // Hoàn vé
  // CHANGE: undefined, // Đổi vé - not specified in doc
} as const;

/**
 * Format rule display text from value and description
 */
const formatRuleDisplayText = (rule: FareFamilyRuleItem): string => {
  const value = rule.value || '';
  const description = rule.description || '';
  if (value && description) {
    return `${value} ${description}`.trim();
  }
  return value || description || rule.fareRuleName || '';
};

/**
 * Extract baggage rules from fare family
 * Uses fareRuleId mapping and checks status === 1 as per business document
 */
export const getBaggageRules = (
  fareFamily: FareFamilyData | null,
): {
  handBaggage?: string;
  checkedBaggage?: string;
} => {
  if (!fareFamily) return {};

  const rules = fareFamily.fareFamilyRules || [];

  // Find rule by fareRuleId and check status === 1
  const findActiveRule = (
    fareRuleId: number,
  ): FareFamilyRuleItem | undefined => {
    return rules.find(r => r.fareRuleId === fareRuleId && r.status === 1);
  };

  // Fallback: try by fareRuleName keyword
  const findByNameKeyword = (
    keyword: string,
  ): FareFamilyRuleItem | undefined => {
    return rules.find(
      r => r.fareRuleName?.toLowerCase().includes(keyword) && r.status === 1,
    );
  };

  // Try fareRuleId first (business doc), then fallback to name keyword
  const handBaggageRule =
    findActiveRule(FARE_RULE_IDS.HAND_BAGGAGE) || findByNameKeyword('xách tay');
  const checkedBaggageRule =
    findActiveRule(FARE_RULE_IDS.CHECKED_BAGGAGE) ||
    findByNameKeyword('ký gửi');

  return {
    handBaggage: handBaggageRule
      ? formatRuleDisplayText(handBaggageRule)
      : undefined,
    checkedBaggage: checkedBaggageRule
      ? formatRuleDisplayText(checkedBaggageRule)
      : undefined,
  };
};

/**
 * Extract fare rules (change/refund) from fare family
 * Uses fareRuleId mapping and checks status === 1 as per business document
 * type: 0 = included/free, 1 = paid/conditional, 2 = not allowed
 */
export const getFareChangeRules = (
  fareFamily: FareFamilyData | null,
): {
  changeRule?: string;
  refundRule?: string;
} => {
  if (!fareFamily) return {};

  const rules = fareFamily.fareFamilyRules || [];

  // Find rule by fareRuleId and check status === 1
  const findActiveRule = (
    fareRuleId: number,
  ): FareFamilyRuleItem | undefined => {
    return rules.find(r => r.fareRuleId === fareRuleId && r.status === 1);
  };

  // Fallback: try by fareRuleName keyword
  const findByNameKeyword = (
    keyword: string,
  ): FareFamilyRuleItem | undefined => {
    return rules.find(
      r => r.fareRuleName?.toLowerCase().includes(keyword) && r.status === 1,
    );
  };

  // Format rule display based on type
  const formatChangeRefundRule = (rule: FareFamilyRuleItem): string => {
    // type: 0 = included/free, 1 = paid/conditional, 2 = not allowed
    if (rule.type === 2) {
      return 'Không được phép';
    }
    if (rule.type === 1) {
      return rule.value || rule.description || 'Có phí';
    }
    // type === 0 - included
    return rule.value || rule.description || 'Miễn phí';
  };

  // Try fareRuleId first for refund (business doc)
  const refundRule =
    findActiveRule(FARE_RULE_IDS.REFUND) || findByNameKeyword('hoàn');
  const changeRule =
    findActiveRule(2) ||
    findByNameKeyword('thay đổi') ||
    findByNameKeyword('đổi');

  return {
    changeRule: changeRule ? formatChangeRefundRule(changeRule) : undefined,
    refundRule: refundRule ? formatChangeRefundRule(refundRule) : undefined,
  };
};

/**
 * Get all rules for display in fare card
 * Mapping fareBasis first 4 chars to farebases.code to find fareFamilyId
 * Fallback: use cabinId if fareBasis not found
 */
export const getFareRulesForDisplay = (
  fareBasis: string,
  fareFamilies: FareFamilyData[],
  cabinId?: number,
): {
  handBaggage?: string;
  checkedBaggage?: string;
  changeRule?: string;
  refundRule?: string;
  allRules: FareFamilyRuleItem[];
  fareFamily?: FareFamilyData;
} => {
  const fareFamily = getFareFamilyByFareBasis(fareBasis, fareFamilies, cabinId);

  if (!fareFamily) {
    return {
      allRules: [],
    };
  }

  const baggageRules = getBaggageRules(fareFamily);
  const fareChangeRules = getFareChangeRules(fareFamily);

  return {
    ...baggageRules,
    ...fareChangeRules,
    allRules: fareFamily.fareFamilyRules || [],
    fareFamily,
  };
};
